import { Common } from '@levelworks/shared-backend-api';
import { OgpApiService } from 'ogp/services/ogp-api-service';
import { Site } from 'shared/hooks';
import { HqApiService } from 'hq/services/hqApiService';
import { PartnerApiService } from 'partner/shared/services/partner-api-service';
import {
  CompanyFlexpools,
  companyFlexpoolsSchema,
  WorkersAvailabilityPaginated,
  workersAvailabilityPaginatedSchema,
} from './availability-service.schema';
import {
  GetWorkerAvailabilityDto,
  GetWorkersAvailabilityDto,
  UpdateWorkersAvailabilityDto,
} from './availability-service.dto';

export type GetWorkersAvailabilityParams = {
  site: Site;
  search: string | undefined;
  fromDate: string;
  flexPoolIds: string[];
  pageParam: number;
  pageSizeParam: number;
};

export class AvailabilityService {
  constructor(
    private readonly ogpApiService: OgpApiService,
    private readonly hqApiService: HqApiService,
    private readonly partnerApiService: PartnerApiService
  ) {}

  public updateWorkerAvailability = async (
    updateDto: UpdateWorkersAvailabilityDto,
    site: Site
  ): Promise<void> => {
    if (site === 'ogp') {
      await this.ogpApiService.update({
        path: `availability/edit`,
        body: updateDto,
      });
    } else if (site === 'hq') {
      await this.hqApiService.update({
        path: `availability/edit`,
        body: updateDto,
      });
    } else {
      throw new Error('Not implemented');
    }
  };

  public getWorkersAvailability = async (
    params: GetWorkersAvailabilityParams
  ): Promise<WorkersAvailabilityPaginated> => {
    const workersAvailability = await this.getWorkersAvailabilityImpl(params);

    return workersAvailabilityPaginatedSchema.parse(workersAvailability);
  };

  public async getCompanyFlexpools(site: Site, companyId?: string): Promise<CompanyFlexpools> {
    let response: Common.GetCompanyFlexPoolsResponseDto;

    if (site === 'hq') {
      response = await this.hqApiService.get<Common.GetCompanyFlexPoolsResponseDto>({
        path: 'availability/flex-pools',
        params: { companyId },
      });
    } else if (site === 'ogp') {
      response = await this.ogpApiService.get<Common.GetCompanyFlexPoolsResponseDto>({
        path: 'availability/flex-pools',
      });
    } else {
      throw new Error('Not implemented');
    }

    return companyFlexpoolsSchema.parse(response);
  }

  private async getWorkersAvailabilityImpl(
    params: GetWorkersAvailabilityParams
  ): Promise<GetWorkersAvailabilityDto> {
    if (params.site === 'ogp') {
      return await this.ogpApiService.get<GetWorkersAvailabilityDto>({
        path: `availability/workers`,
        params: {
          search: params.search,
          pageParam: params.pageParam,
          pageSizeParam: params.pageSizeParam,
          fromDate: params.fromDate,
          flexPoolIds: params.flexPoolIds,
        },
      });
    }
    if (params.site === 'hq') {
      return await this.hqApiService.get<GetWorkersAvailabilityDto>({
        path: `availability/flexworkers`,
        params: {
          search: params.search,
          pageParam: params.pageParam,
          pageSizeParam: params.pageSizeParam,
          fromDate: params.fromDate,
          flexPoolIds: params.flexPoolIds,
        },
      });
    }
    if (params.site === 'partner') {
      return await this.partnerApiService.get<GetWorkersAvailabilityDto>({
        path: `availability/flexworkers`,
        params: {
          search: params.search,
          pageParam: params.pageParam,
          pageSizeParam: params.pageSizeParam,
          fromDate: params.fromDate,
        },
      });
    }

    throw new Error('Not implemented');
  }
  public async getWorkerAvailability(
    site: Site,
    workerId: string | null,
    fromDate: string,
    toDate: string
  ): Promise<GetWorkerAvailabilityDto> {
    if (site === 'ogp' && workerId) {
      return await this.ogpApiService.get<GetWorkerAvailabilityDto>({
        path: `availability/${workerId}`,
        params: {
          fromDate,
          toDate,
        },
      });
    } else if (site === 'hq' && workerId) {
      return await this.hqApiService.get<GetWorkerAvailabilityDto>({
        path: `availability/${workerId}`,
        params: {
          fromDate,
          toDate,
        },
      });
    } else {
      throw new Error('not implemented');
    }
  }
}
